import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocale, useTranslations } from "use-intl";

import { useCurrency } from "@/hooks/use-currency";
import { useViewer } from "@/hooks/use-viewer";
import { setLocale } from "@/i18n/actions";
import { setCurrencyPreference } from "@/i18n/currency-actions";
import { getMessagesForRoute } from "@/i18n/get-messages-for-route";
import { resolveSignInLocale } from "@/i18n/resolveSignInLocale";
import {
  trackAccountCreation,
  trackLogin,
  trackLoginStarted,
  trackSignupStarted,
} from "@/lib/analytics";
import { authClient } from "@/lib/auth-client";
import {
  clearPendingPhoneVerification,
  readPendingPhoneVerification,
  writePendingPhoneVerification,
} from "@/lib/pendingPhoneVerification";
import { buildOnboardingHrefWithRedirect, getRedirectAfterLogin } from "@/lib/redirect-after-login";
import { verifyPhoneOtp } from "@/lib/verify-phone-otp";
import { invalidateSession } from "@/utils/session";
import { type CountryCode } from "@garimpa/lib/countries";
import { normalizePhoneNumber } from "@garimpa/lib/phoneNumber";
import { normalizeViewerCurrency, type ViewerLocale } from "@garimpa/lib/viewerPreferences";

export interface PhoneOtpAuthOptions {
  initiallyExpanded?: boolean;
  pendingVerificationStorageScope?: string;
  redirectAfterLogin?: string;
  mode: "signin" | "signup" | "link";
  onLinked?: () => Promise<void> | void;
}

const RESEND_DELAY_SECONDS = 60;

function remainingResendSeconds(resendAvailableAt: number) {
  return Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000));
}

async function completeVerifiedPhoneAuth(input: {
  mode: PhoneOtpAuthOptions["mode"];
  currentLocale: ViewerLocale;
  onLinked: PhoneOtpAuthOptions["onLinked"];
  redirectAfterLogin: string | undefined;
  userId: string | null | undefined;
  userLanguage: string | null | undefined;
  userCurrency: string | null | undefined;
  navigate: (href: string) => Promise<unknown>;
  messages: { linked: string; signedIn: string; signedUp: string };
}) {
  if (input.mode === "link") {
    await input.onLinked?.();
    toast.success(input.messages.linked);
    return;
  }

  if (input.mode === "signup" && input.userId) {
    trackAccountCreation("phone", input.userId);
  } else if (input.mode === "signin") {
    trackLogin("phone");
  }
  await setLocale({
    data: resolveSignInLocale({
      currentLocale: input.currentLocale,
      userLanguage: input.userLanguage,
    }),
  });
  const userCurrency = normalizeViewerCurrency(input.userCurrency);
  if (userCurrency) await setCurrencyPreference({ data: userCurrency });

  const redirect = getRedirectAfterLogin(input.redirectAfterLogin);
  const destination =
    input.mode === "signup" ? buildOnboardingHrefWithRedirect(redirect) : redirect;
  await input.navigate(destination);
  toast.success(input.mode === "signup" ? input.messages.signedUp : input.messages.signedIn);
}

async function prepareVerifiedPhoneLocale(input: {
  mode: PhoneOtpAuthOptions["mode"];
  currentLocale: ViewerLocale;
  userLanguage: string | null | undefined;
  errorMessage: string;
}) {
  if (input.mode === "link") return;
  const locale = resolveSignInLocale({
    currentLocale: input.currentLocale,
    userLanguage: input.userLanguage,
  });
  await getMessagesForRoute(locale).catch((error: unknown) => {
    toast.error(input.errorMessage);
    throw error;
  });
}

export function usePhoneOtpAuth(options: PhoneOtpAuthOptions) {
  const t = useTranslations("globals.auth");
  const tError = useTranslations("globals.error");
  const locale = useLocale() as ViewerLocale;
  const { currency } = useCurrency();
  const viewer = useViewer();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(
    options.mode === "link" || options.initiallyExpanded === true,
  );
  const [phone, setPhone] = useState("");
  const [phoneCountryOverride, setPhoneCountryOverride] = useState<CountryCode | null>(null);
  const [normalizedPhone, setNormalizedPhone] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const countdownActive = resendSeconds > 0;
  const phoneCountry = phoneCountryOverride ?? viewer.preferences.country;

  useEffect(() => {
    if (!countdownActive) return;
    const timer = window.setInterval(
      () => setResendSeconds((value) => Math.max(0, value - 1)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [countdownActive]);

  useEffect(() => {
    const pending = readPendingPhoneVerification(options.pendingVerificationStorageScope);
    if (!pending) return;

    const normalized = normalizePhoneNumber(pending.phone, pending.country);
    if (!normalized) {
      clearPendingPhoneVerification(options.pendingVerificationStorageScope);
      return;
    }

    setPhone(pending.phone);
    setPhoneCountryOverride(pending.country);
    setNormalizedPhone(normalized);
    setResendSeconds(remainingResendSeconds(pending.resendAvailableAt));
  }, [options.pendingVerificationStorageScope]);

  async function sendCode() {
    const normalized = normalizePhoneNumber(phone, phoneCountry);
    if (!normalized) {
      toast.error(t("invalidPhoneNumber"));
      return;
    }

    setSending(true);
    try {
      const result = await authClient.phoneNumber.sendOtp(
        { phoneNumber: normalized },
        { headers: { "accept-language": locale } },
      );
      if (result.error) {
        toast.error(result.error.message ?? t("phoneCodeSendError"));
        return;
      }
      setNormalizedPhone(normalized);
      const resendAvailableAt = Date.now() + RESEND_DELAY_SECONDS * 1000;
      setResendSeconds(remainingResendSeconds(resendAvailableAt));
      writePendingPhoneVerification(options.pendingVerificationStorageScope, {
        country: phoneCountry,
        phone,
        resendAvailableAt,
      });
      if (options.mode === "signup") {
        trackSignupStarted("phone", "verification");
      } else if (options.mode === "signin") {
        trackLoginStarted("phone", "verification");
      }
      toast.success(t("phoneCodeSent"));
    } finally {
      setSending(false);
    }
  }

  async function verifyCode() {
    if (!normalizedPhone) return;

    setVerifying(true);
    try {
      const result = await verifyPhoneOtp({
        phoneNumber: normalizedPhone,
        code,
        mode: options.mode,
        language: locale,
        currency,
      });
      if (result.error) {
        toast.error(result.error.message ?? t("phoneCodeInvalid"));
        return;
      }

      const {
        id: userId,
        language: userLanguage,
        currency: userCurrency,
      } = result.data?.user ?? {};
      clearPendingPhoneVerification(options.pendingVerificationStorageScope);
      await prepareVerifiedPhoneLocale({
        mode: options.mode,
        currentLocale: locale,
        userLanguage,
        errorMessage: tError("message"),
      });
      await invalidateSession(queryClient);
      await router.invalidate();
      await completeVerifiedPhoneAuth({
        mode: options.mode,
        currentLocale: locale,
        onLinked: options.onLinked,
        redirectAfterLogin: options.redirectAfterLogin,
        userId,
        userLanguage,
        userCurrency,
        navigate: (href) => router.navigate({ href }),
        messages: {
          linked: t("phoneLinked"),
          signedIn: t("signInSuccess"),
          signedUp: t("signUpSuccess"),
        },
      });
    } finally {
      setVerifying(false);
    }
  }

  function resetVerification() {
    clearPendingPhoneVerification(options.pendingVerificationStorageScope);
    setNormalizedPhone(null);
    setCode("");
    setResendSeconds(0);
  }

  return {
    available: viewer.capabilities.whatsappAuthAvailable,
    expanded,
    expand: () => setExpanded(true),
    collapse: () => setExpanded(false),
    phone,
    setPhone,
    phoneCountry,
    setPhoneCountry: setPhoneCountryOverride,
    normalizedPhone,
    code,
    setCode,
    sending,
    verifying,
    resendSeconds,
    resetVerification,
    sendCode,
    verifyCode,
  };
}
